import { httpClient } from '@/utils/httpClient';
import type {
  IOcrTestCase,
  IOcrTestExpectedField,
  IOcrTestRun,
  IOcrTestRunResult,
} from '@/types';

const BASE = '/admin/ocr-tests';

/* ── Test Cases ── */

interface ListCasesParams {
  page?: number;
  pageSize?: number;
  operationType?: number;
  nationalityCode?: string;
  isValidated?: boolean | '';
}

export interface PaginatedResponse<T> {
  data: T[];
  recordsTotal: number;
  recordsFiltered: number;
}

export const ocrTestingService = {
  /* Cases */
  listCases: (params: ListCasesParams = {}) => {
    // Backend reads DataTables convention: draw / start / length / searchDocumentType
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 25;
    const qs = new URLSearchParams();
    qs.set('draw', '1');
    qs.set('start', String((page - 1) * pageSize));
    qs.set('length', String(pageSize));
    if (params.operationType) qs.set('searchDocumentType', String(params.operationType));
    return httpClient.get<PaginatedResponse<IOcrTestCase>>(
      `${BASE}/cases?${qs.toString()}`
    );
  },

  getCase: (id: string) =>
    httpClient.get<IOcrTestCase>(`${BASE}/cases/${id}`),

  createCase: (formData: FormData) =>
    httpClient.post<IOcrTestCase>(`${BASE}/cases`, formData),

  updateCase: (id: string, data: Partial<IOcrTestCase>) =>
    httpClient.put<IOcrTestCase>(`${BASE}/cases/${id}`, data),

  deleteCase: (id: string) =>
    httpClient.delete<void>(`${BASE}/cases/${id}`),

  /* Images */
  getImageUrl: (caseId: string, imageId: string) =>
    `${BASE}/cases/${caseId}/images/${imageId}`,

  uploadImage: (caseId: string, formData: FormData) =>
    httpClient.post<void>(`${BASE}/cases/${caseId}/images`, formData),

  deleteImage: (caseId: string, imageId: string) =>
    httpClient.delete<void>(`${BASE}/cases/${caseId}/images/${imageId}`),

  /* Expected Fields */
  getFields: (caseId: string) =>
    httpClient.get<IOcrTestExpectedField[]>(`${BASE}/cases/${caseId}/fields`),

  saveFields: (caseId: string, fields: IOcrTestExpectedField[]) =>
    httpClient.put<void>(`${BASE}/cases/${caseId}/fields`, fields),

  validateCase: (caseId: string) =>
    httpClient.post<void>(`${BASE}/cases/${caseId}/validate`),

  captureBaseline: (caseId: string) =>
    httpClient.post<IOcrTestExpectedField[]>(
      `${BASE}/cases/${caseId}/capture-baseline`
    ),

  /* Runs */
  runAll: (operationType?: number) => {
    const qs = operationType ? `?operationType=${operationType}` : '';
    return httpClient.post<IOcrTestRun>(`${BASE}/run${qs}`);
  },

  listRuns: (page = 1, pageSize = 25) => {
    const qs = new URLSearchParams({
      draw: '1',
      start: String((page - 1) * pageSize),
      length: String(pageSize),
    });
    return httpClient.get<PaginatedResponse<IOcrTestRun>>(
      `${BASE}/runs?${qs.toString()}`
    );
  },

  getRun: (id: string) =>
    httpClient.get<IOcrTestRun>(`${BASE}/runs/${id}`),

  getRunResults: (runId: string) =>
    httpClient.get<IOcrTestRunResult[]>(`${BASE}/runs/${runId}/results`),
};
